"use client";

import { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import LoginButton from "@/components/LoginButton";

export default function LoginLanding() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const loginBtnRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  const handleSignUp = () => {
    // Store password in sessionStorage (never in URL) to pass to registration page
    if (password) sessionStorage.setItem("reg_password", password);
    const params = new URLSearchParams();
    if (email) params.set("email", email);
    router.push(`/auth/register?${params.toString()}`);
  };

  const brand = {
    color: "#6c47ff",
    text: "#000000",
    muted: "rgba(0,0,0,0.5)",
    border: "rgba(0,0,0,0.06)",
  } as const;

  return (
    <main
      className="section-lines"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f6f7",
        color: brand.text,
        padding: "24px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 720,
          display: "flex",
          flexDirection: "column",
          gap: 28,
          textAlign: "center",
        }}
      >
        {/* 1) Hero / Intro */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.05em', color: brand.color, textTransform: 'uppercase' as const }}>
            Refinea
          </div>
          <h1 style={{ fontSize: 40, lineHeight: 1.12, fontWeight: 700, letterSpacing: '-0.03em' }}>
            Monitor your brand inside Generative AI models.
          </h1>
          <p style={{ fontSize: 16, color: 'rgba(0, 0, 0, 0.4)', fontWeight: 500, letterSpacing: '-0.01em' }}>
            Refinea shows you how Large Language Models mention your brand and helps you improve your visibility.
          </p>

          {/* Logos row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 40,
              marginTop: 6,
              opacity: 0.75,
            }}
          >
            <Image src="/LLMs/claude.svg" alt="Claude" width={36} height={36} style={{ filter: "grayscale(1)" }} />
            <Image src="/LLMs/gemini.svg" alt="Gemini" width={36} height={36} style={{ filter: "grayscale(1)" }} />
            <Image src="/LLMs/openai.svg" alt="ChatGPT" width={36} height={36} style={{ filter: "grayscale(1)" }} />
            <Image src="/LLMs/perplexity.svg" alt="Perplexity" width={36} height={36} style={{ filter: "grayscale(1)" }} />
            <Image src="/LLMs/deepseek.svg" alt="DeepSeek" width={36} height={36} style={{ filter: "grayscale(1)" }} />
          </div>
        </div>

        {/* 2) Primary CTA */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loginBtnRef.current?.click();
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            marginTop: 8,
          }}
        >
          {/* Email Field */}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: "100%",
              maxWidth: 360,
              padding: "12px 16px",
              borderRadius: 10,
              border: '0.5px solid rgba(0, 0, 0, 0.08)',
              background: "#fff",
              color: brand.text,
              fontSize: 15,
              fontWeight: 400,
              outline: "none",
              boxShadow: "none",
              transition: 'border-color 0.15s ease',
            }}
          />

          {/* Password Field */}
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              width: "100%",
              maxWidth: 360,
              padding: "12px 16px",
              borderRadius: 10,
              border: '0.5px solid rgba(0, 0, 0, 0.08)',
              background: "#fff",
              color: brand.text,
              fontSize: 15,
              fontWeight: 400,
              outline: "none",
              boxShadow: "none",
              transition: 'border-color 0.15s ease',
            }}
          />

          <input type="submit" hidden />

          <LoginButton
            ref={loginBtnRef}
            label="Login"
            email={email}
            password={password}
            style={{
              width: "100%",
              maxWidth: 360,
              padding: "12px 16px",
              borderRadius: 10,
              border: 'none',
              background: brand.color,
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(108, 71, 255, 0.25)",
              letterSpacing: '-0.01em',
            }}
          />

          <button
            type="button"
            onClick={handleSignUp}
            style={{
              width: "100%",
              maxWidth: 360,
              padding: "12px 16px",
              borderRadius: 10,
              border: '0.5px solid rgba(108, 71, 255, 0.3)',
              background: "#fff",
              color: brand.color,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "none",
              textDecoration: "none",
              display: "inline-block",
              textAlign: "center",
              transition: "all 150ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(108, 71, 255, 0.03)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
            }}
          >
            Sign Up
          </button>
        </form>

        {/* 3) Context (optional) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          <div style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.35)', fontWeight: 500 }}>
            Currently in trial — available for early access partners.
          </div>
          <div>
            <Link
              href="https://refinea.io"
              style={{ color: brand.color, fontWeight: 600 }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about Refinea
            </Link>
          </div>
        </div>

      </div>

      {/* Bottom divider + Contact Us text link */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(255, 255, 255, 0.8)",
          backdropFilter: 'blur(8px)',
          borderTop: '0.5px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <div
          style={{
            padding: "10px 0",
            width: "100%",
          }}
        >
          <a
            href="mailto:info@refinea.io"
            style={{
              color: brand.color,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "none",
              display: "inline-block",
              marginLeft: 32,
            }}
          >
            Contact Us
          </a>
        </div>
      </div>
    </main>
  );
}
